export default {
  // THE HOME'S CLOUDS DRIFT (DESIGN-WL-BOOT-LOADER-MOTION-1, Nika 2026-09-28
  // "make clouds animation too"): a layer twice the viewport wide slides left
  // by half its width — exactly one tile of the seamless clouds mask — so the
  // loop has no seam. Transform only: no mask position, no layout.
  ambientDrift: {
    from: { transform: 'translateX(0)' },
    to: { transform: 'translateX(-50%)' }
  },
  // Animate the inner mark, leaving its centering transform untouched.
  loaderLogo: {
    '0%, 100%': { transform: 'rotate(0deg) scale(1)', opacity: '1' },
    '50%': { transform: 'rotate(-4deg) scale(0.96)', opacity: '0.86' }
  },
  loaderProgress: {
    '0%, 100%': { opacity: '0.65' },
    '50%': { opacity: '1' }
  },
  // /infra deployment-canvas edges: a slow dash flow to read as live traffic.
  infraFlow: {
    from: { strokeDashoffset: '24' },
    to: { strokeDashoffset: '0' }
  },
  // /infra detail drawer: slide-in from the left edge on open.
  fadeInLeft: {
    from: { opacity: '0', transform: 'translateX(-24px)' },
    to: { opacity: '1', transform: 'translateX(0)' }
  },
  // Messenger list (left-docked chat panel): short horizontal slide-in from
  // the left edge on open. X-axis mirror of brand's `fadeInUpShort` (same
  // 6.5% travel + opacity fade, axis flipped Y→X with a negative start so a
  // left-docked panel enters from its own edge). Distinct name to avoid the
  // brand `fadeInLeft` collision (that one starts to the right, +12.5%).
  fadeInLeftShort: {
    from: { opacity: '0', transform: 'translate3d(-6.5%, 0, 0)' },
    to: { opacity: '1', transform: 'translate3d(0, 0, 0)' }
  },
  // …and slide-out to the right on close.
  fadeOutRight: {
    from: { opacity: '1', transform: 'translateX(0)' },
    to: { opacity: '0', transform: 'translateX(24px)' }
  },
  // White-label loader mark: a 3D coin flip about the Y axis (Nika: "dont
  // make bellforge loading animation - logo rotating - make it flipping").
  // Perspective INSIDE the transform so the keyframes are self-contained —
  // no parent perspective property to thread through the loader chrome.
  // Distinct name, no brand collision (brand has `rotate`, no flip).
  // ⚠️ STILL THE DEFAULT BOOT MOTION for every white-label tenant, and still
  // used by the shared WhitelabelSignin persona tile. The `dial*` set below is
  // OPT-IN per org record (`branding.loaderMotion === 'dial'`) — Nika
  // 2026-09-10: "change loading spinner only for vault - keep bellforge and
  // gita as was". Deleting this set changes every tenant's boot.
  flipY: {
    from: { transform: 'perspective(720px) rotateY(0deg)' },
    to: { transform: 'perspective(720px) rotateY(360deg)' }
  },

  // ── The white-label boot mark: a VAULT DIAL ──────────────────────────────
  // OPT-IN, per org record: only a tenant whose public branding carries
  // `loaderMotion: 'dial'` boots with this. Everyone else keeps `flipY` above.
  // Three keyframe sets, one motion. The org's mark behaves like the dial of
  // a combination lock: it arrives, turns right, turns a full turn left, then
  // turns right onto the number and clicks home. It is the first thing a
  // partner sees on every load, so it has to read as mechanism, not as a
  // spinner: every segment has a destination and every destination is held.
  //
  // ⚠️ NET ROTATION PER CYCLE IS EXACTLY 360° (+270 −360 +90 +6 −6). The loop
  // therefore restarts on the angle it ended on and the seam is invisible on
  // ANY mark. The obvious sequence (+270 −180 +90) nets 180°, which is
  // seamless only for a mark that is 180°-symmetric — true of the Vault dial
  // disc, false of a lettered or asymmetric mark, and this loader is shared
  // by every white-label tenant.
  //
  // One easing family, no per-stop overrides: CSS applies
  // `animation-timing-function` to EVERY keyframe interval, so the single
  // `defaultBezier` on the element gives each turn the house ease-out
  // (fast off the mark, long glide into the stop) and leaves the holds flat.
  //
  // Reduced motion: the global guard in brand/designSystem/reset.js collapses
  // every animation to 0.01ms / 1 iteration. Each set below is written so the
  // resting state is CORRECT under that collapse — see the fill-mode notes.

  // 1. Arrival. Runs ONCE, not per cycle. A boot mark that fades to nothing
  //    every three seconds reads as a rendering fault rather than as work in
  //    progress — the same reasoning brand's `pulse` carries against
  //    `blinking`. Last keyframe is the neutral state, so css-in-props picks
  //    `backwards` and the collapsed (reduced-motion) run rests at full ink.
  dialEnter: {
    from: { transform: 'scale(0.85)', opacity: '0' },
    to: { transform: 'scale(1)', opacity: '1' }
  },

  // 2. The turn. Percentages ARE the choreography (cycle ≈ 3s via timing
  //    token `K1`): turn right to 270°, hold, a full turn left to −90°, hold,
  //    right onto the mark with a 6° overshoot, then settle — the click — and
  //    rest before the next attempt. Rotation only: no vertical bounce, no
  //    scale, nothing that would make the mark breathe.
  dialTurn: {
    '0%': { transform: 'rotate(0deg)' },
    '24%': { transform: 'rotate(270deg)' },
    '32%': { transform: 'rotate(270deg)' },
    '62%': { transform: 'rotate(-90deg)' },
    '70%': { transform: 'rotate(-90deg)' },
    '88%': { transform: 'rotate(6deg)' },
    '94%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(0deg)' }
  },

  // 3. The ring, on the SAME stops as the turn: it advances while the dial
  //    moves and pauses while the dial holds, so the ring reads as the dial's
  //    own progress record and not as a second, unrelated spinner. Offsets
  //    are the circumference of the r=42 circle in the 88-unit viewBox
  //    (2·π·42 ≈ 264) at 0% / 45% / 80% / 100% drawn.
  //    ⚠️ The consumer sets `animationFillMode: 'none'` for this one: the last
  //    keyframe is transparent (the completed ring dims away before the next
  //    fill starts), and the css-in-props default of `both` would freeze a
  //    reduced-motion visitor on that transparent frame — an invisible ring.
  //    With `none` the collapsed run falls back to the element's own
  //    presentation attributes: a full ring.
  dialRingFill: {
    '0%': { strokeDashoffset: '264', opacity: '1' },
    '24%': { strokeDashoffset: '145' },
    '32%': { strokeDashoffset: '145' },
    '62%': { strokeDashoffset: '53' },
    '70%': { strokeDashoffset: '53' },
    '88%': { strokeDashoffset: '0', opacity: '1' },
    '100%': { strokeDashoffset: '0', opacity: '0' }
  }
}
